import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { FaChartBar, FaUsers, FaLayerGroup } from 'react-icons/fa';

const ACRONYMS = new Set(['IAP', 'UX', 'UI', 'API', 'SDK', 'ID', 'URL', 'DAU', 'WAU', 'MAU']);

const CANONICAL_FEATURE_KEYS = {
  flow_orchestration: 'flow_orchestrator',
  visibility: 'visibility_configuration'
};

const FEATURE_NAME_OVERRIDES = {
  analytics_iap: 'Advanced Analytics',
  iap_usage_analytics: 'Advanced Analytics',
  advanced_analytics: 'Advanced Analytics',
  ux_customizer: 'UX Customizer',
  flow_orchestrator: 'Flow Orchestrator',
  flow_orchestration: 'Flow Orchestrator',
  visibility: 'Visibility Configuration',
  visibility_configuration: 'Visibility Configuration',
  browser_extension_runtime: 'Browser Extension Runtime',
  customer_analytics: 'Customer Analytics',
  survey_insights: 'Survey Insights',
  flow_preview: 'Flow Preview'
};

export const formatFeatureDisplayName = (featureKey, flows = []) => {
  if (!featureKey) return 'Unknown Feature';
  // Check if it matches a known flow GUID
  const match = (flows || []).find((f) => String(f.id) === String(featureKey));
  if (match) {
    return match.flowName || match.name || featureKey;
  }
  const lower = String(featureKey).toLowerCase().trim();
  if (FEATURE_NAME_OVERRIDES[lower]) {
    return FEATURE_NAME_OVERRIDES[lower];
  }
  // Title-case snake_case/kebab-case strings with acronym preservation
  return featureKey
    .split(/[_,-]+/)
    .map((word) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

const CustomTooltip = ({ active, payload, metricKey }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    const adoptionPct = Math.round((d.adoptionRate || 0) * 100);
    return (
      <div className="analytics-chart-tooltip" data-testid="top-features-tooltip">
        <p className="tooltip-title">{d.displayName}</p>
        <p className="tooltip-id">Key: <code>{d.featureKey}</code></p>
        <div className="tooltip-highlight">
          <strong>
            {metricKey === 'activeUsers'
              ? `${d.activeUsers.toLocaleString()} ${d.activeUsers === 1 ? 'user' : 'users'}`
              : `${d.sessions.toLocaleString()} ${d.sessions === 1 ? 'session' : 'sessions'}`}
          </strong>
          {d.adoptionRate != null && (
            <span className="highlight-sub"> ({adoptionPct}% adoption)</span>
          )}
        </div>
        <div className="tooltip-metrics-grid">
          <div>
            <span className="metric-label">Active Users:</span>
            <strong className="metric-val">{d.activeUsers.toLocaleString()}</strong>
          </div>
          <div>
            <span className="metric-label">Total Sessions:</span>
            <strong className="metric-val">{d.sessions.toLocaleString()}</strong>
          </div>
          <div>
            <span className="metric-label">Adoption Rate:</span>
            <strong className="metric-val">{(d.adoptionRate * 100).toFixed(1)}%</strong>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  metricKey: PropTypes.string
};

export default function TopFeaturesHorizontalBar({ features = [], flows = [], height = 360 }) {
  const [metricKey, setMetricKey] = useState('activeUsers'); // 'activeUsers' or 'sessions'

  const chartData = useMemo(() => {
    if (!Array.isArray(features) || features.length === 0) return [];

    const aggregated = new Map();

    features.forEach((item) => {
      const rawKey = item.featureKey || item.flowId || '';
      const canonicalKey = CANONICAL_FEATURE_KEYS[rawKey] || rawKey;
      const displayName = formatFeatureDisplayName(canonicalKey, flows);

      const activeUsers = Number(item.activeUsers || 0);
      const sessions = Number(item.sessions || 0);
      const adoptionRate = Number(item.adoptionRate || 0);

      const existing = aggregated.get(displayName);
      if (existing) {
        // Merge duplicate features emitted under slight naming variations
        existing.activeUsers = Math.max(existing.activeUsers, activeUsers);
        existing.sessions = Math.max(existing.sessions, sessions);
        existing.adoptionRate = Math.max(existing.adoptionRate, adoptionRate);
      } else {
        aggregated.set(displayName, {
          featureKey: canonicalKey,
          displayName,
          activeUsers,
          sessions,
          adoptionRate
        });
      }
    });

    return Array.from(aggregated.values())
      .sort((a, b) => b[metricKey] - a[metricKey])
      .slice(0, 10);
  }, [features, flows, metricKey]);

  if (chartData.length === 0) {
    return (
      <div className="chart-empty-state" data-testid="top-features-empty">
        <FaChartBar className="empty-icon" />
        <p>No feature adoption metrics recorded in this window.</p>
      </div>
    );
  }

  const primaryColor = metricKey === 'activeUsers' ? '#6366f1' : '#0ea5e9';

  const subtitle = metricKey === 'activeUsers'
    ? 'Features with the highest number of unique active users'
    : 'Features with the most user sessions';

  return (
    <div className="top-features-chart-card" data-testid="top-features-horizontal-bar">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-card-title">Top Features by Engagement</h3>
          <p className="chart-card-subtitle">{subtitle}</p>
        </div>
        <div className="metric-toggle-group" role="group" aria-label="Toggle metric">
          <button
            type="button"
            className={`toggle-btn ${metricKey === 'activeUsers' ? 'active' : ''}`}
            onClick={() => setMetricKey('activeUsers')}
            data-testid="toggle-metric-users"
          >
            <FaUsers className="toggle-btn-icon" /> Users
          </button>
          <button
            type="button"
            className={`toggle-btn ${metricKey === 'sessions' ? 'active' : ''}`}
            onClick={() => setMetricKey('sessions')}
            data-testid="toggle-metric-sessions"
          >
            <FaLayerGroup className="toggle-btn-icon" /> Sessions
          </button>
        </div>
      </div>

      <div className="chart-wrapper" style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 10, right: 60, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis
              type="number"
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
              tick={{ fill: '#64748b', fontSize: 12 }}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="displayName"
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
              tick={{ fill: '#334155', fontSize: 12, fontWeight: 500 }}
              width={165}
            />
            <Tooltip
              content={<CustomTooltip metricKey={metricKey} />}
              cursor={{ fill: 'rgba(99, 102, 241, 0.05)' }}
            />
            <Bar dataKey={metricKey} radius={[0, 6, 6, 0]} maxBarSize={24} fill={primaryColor}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${entry.featureKey}-${index}`}
                  fill={index === 0 ? primaryColor : `${primaryColor}cc`}
                />
              ))}
              <LabelList
                dataKey={metricKey}
                position="right"
                offset={10}
                style={{ fill: '#334155', fontSize: 12, fontWeight: 700 }}
                formatter={(val) => Number(val || 0).toLocaleString()}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

TopFeaturesHorizontalBar.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      featureKey: PropTypes.string,
      activeUsers: PropTypes.number,
      sessions: PropTypes.number,
      adoptionRate: PropTypes.number
    })
  ),
  flows: PropTypes.array,
  height: PropTypes.number
};
